import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { supabase } from '@/lib/supabase';
import Index from '@/pages/Index';
import HomePage from '@/pages/HomePage';

import Subscriptions from '@/pages/Subscriptions';
import SubscriptionDetail from '@/pages/SubscriptionDetail';
import SubscriptionItemDetail from '@/pages/SubscriptionItemDetail';
import InvoiceDetail from '@/pages/InvoiceDetail';
import Customers from '@/pages/Customers';
import CustomerDetail from '@/pages/CustomerDetail';
import Vendors from '@/pages/Vendors';
import VendorForm from '@/pages/VendorForm';
import VendorDetail from '@/pages/VendorDetail';
import Users from '@/pages/Users';
import NotFound from '@/pages/NotFound';
import Login from '@/pages/auth/Login';
import ResetPassword from '@/pages/auth/ResetPassword';
import UpdatePassword from '@/pages/auth/UpdatePassword';
import Callback from '@/pages/auth/Callback';
import SubscriptionPlans from '@/pages/SubscriptionPlans';
import EditSubscriptionPlan from '@/pages/subscription-plans/EditSubscriptionPlan';
import NewSubscriptionPlan from '@/pages/subscription-plans/NewSubscriptionPlan';
import SubscriptionPlanDetails from '@/pages/subscription-plans/SubscriptionPlanDetails';
import SubscriptionPlanFamilies from '@/pages/SubscriptionPlanFamilies';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import SubscriptionWizardPage from '@/pages/SubscriptionWizardPage';
import SubscriptionConfirmation from '@/components/subscription-wizard/SubscriptionConfirmation';
import Integrations from '@/pages/Integrations';
import Courses from '@/pages/Courses';
import CourseForm from '@/pages/CourseForm';

import CourseFamilies from '@/pages/CourseFamilies';
import Classes from '@/pages/Classes';
import ClassSessions from '@/pages/ClassSessions';
import SessionsCalendar from '@/pages/SessionsCalendar';
import ClassSponsors from '@/pages/ClassSponsors';
import Instructors from '@/pages/Instructors';
import ClassDetails from '@/pages/ClassDetails';
import ClassWizardPage from '@/pages/ClassWizardPage';
import ClassConfirmationPage from '@/pages/ClassConfirmationPage';
import CourseDetail from '@/pages/CourseDetail';
import ClassesDashboard from '@/pages/ClassesDashboard';
import UnifiedDashboard from '@/pages/UnifiedDashboard';
import EmailTemplates from '@/pages/EmailTemplates';
import TransactionalEmails from '@/pages/TransactionalEmails';
import EmailTemplateCreate from '@/pages/EmailTemplateCreate';
import EmailTemplateEdit from '@/pages/EmailTemplateEdit';
import AddTemplateVersion from '@/pages/AddTemplateVersion';
import ReactEmailPreview from '@/pages/ReactEmailPreview';
import AccessControl from '@/pages/AccessControl';
import Unauthorized from '@/pages/Unauthorized';
import SystemJobs from '@/pages/SystemJobs';
import SystemOrders from '@/pages/SystemOrders';
import SubscriptionWizardStep1 from '@/pages/subscriptions/wizard/Step1';
import Invoices from '@/pages/Invoices';
import EconomicIndicesPage from '@/pages/subscriptions/EconomicIndices';
import ImportSubscriptionsPlans from '@/pages/ImportSubscriptionsPlans';
import TaskManagementPage from '@/pages/task-management/index';
import TaskSettingsPage from '@/pages/task-management/settings';
import TaskLabelsPage from '@/pages/task-management/labels';
import Transactions from '@/pages/Transactions';
import VoucherCodes from '@/pages/VoucherCodes';
import MaximizedLayout from '@/components/layout/MaximizedLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BusinessOpportunities from '@/pages/BusinessOpportunities';
import BusinessOpportunityForm from '@/pages/BusinessOpportunityForm';
import BusinessOpportunityDetail from '@/pages/BusinessOpportunityDetail';
import OpportunityDetailSimple from '@/pages/OpportunityDetailSimple';
import ProposalDetail from '@/pages/ProposalDetail';
import PublicProposalView from '@/pages/PublicProposalView';
import OpportunityWizardPage from '@/pages/OpportunityWizardPage';
import OpportunityConfirmation from '@/components/opportunity-wizard/OpportunityConfirmation';

import ContractDetail from '@/pages/ContractDetail';
import Contracts from '@/pages/Contracts';
import ContractTemplates from '@/pages/ContractTemplates';
import ContractTemplateForm from '@/pages/ContractTemplateForm';
import ProposalTemplates from '@/pages/ProposalTemplates';
import ProposalTemplateForm from '@/pages/ProposalTemplateForm';
import PlaybookPublicView from '@/pages/customer-success/playbooks/PlaybookPublicView';
import PlaybooksCustomerSuccessPage from '@/pages/customer-success/playbooks/Playbooks';
import EditPlaybookTemplate from '@/pages/customer-success/playbooks/EditPlaybookTemplate';
import NewPlaybookTemplate from '@/pages/customer-success/playbooks/NewPlaybookTemplate';
import PlaybookRunDetail from '@/pages/customer-success/playbooks/PlaybookRunDetail';
import PlaybookRuns from '@/pages/customer-success/playbooks/PlaybookRuns';
import NewPlaybookRun from '@/pages/customer-success/playbooks/NewPlaybookRun';
import SystemNotifications from '@/pages/SystemNotifications';
// Import support pages
import SupportInbox from '@/pages/SupportInbox';
import SupportTickets from '@/pages/SupportTickets';
import LoadingExamples from '@/pages/loading-examples';

// Enhanced Protected route wrapper with better session handling
function ProtectedRoute({ children, path }: { children: React.ReactNode, path?: string }) {
  const { user, loading } = useAuthContext();
  
  // If we already have a user from the context, grant access immediately.
  // This prevents unmounting children components when navigating between protected routes.
  if (user) {
    console.log('✅ ProtectedRoute: User found in context, access granted immediately.');
    return <DashboardLayout>{children}</DashboardLayout>;
  }

  // If the auth context is still loading, show a loading indicator.
  if (loading) {
    console.log('⏳ ProtectedRoute: Auth context is loading, showing loading indicator.');
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // If there's no user and loading is complete, it means the user is not logged in.
  // Redirect to the login page.
  console.log('🚪 ProtectedRoute: No user found after loading, redirecting to login.');
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { user, loading } = useAuthContext();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/auth/callback" element={<Callback />} />
      
      {/* New sessions calendar */}
      <Route path="/sessions-calendar" element={
        <ProtectedRoute path="/sessions-calendar">
          <SessionsCalendar />
        </ProtectedRoute>
      } />

      {/* Public playbook view for clients */}
      <Route path="/p/:id/:token" element={<PlaybookPublicView />} />

      {/* Public proposal views */}
      <Route path="/public-proposal/:token" element={<PublicProposalView />} />

      {/* Support routes */}
      <Route
        path="/support/inbox"
        element={
          <ProtectedRoute path="/support/inbox">
            <SupportInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/tickets"
        element={
          <ProtectedRoute path="/support/tickets">
            <SupportTickets />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <UnifiedDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute path="/customers">
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute path="/customers">
            <CustomerDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute path="/vendors">
            <Vendors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/new"
        element={
          <ProtectedRoute path="/vendors">
            <VendorForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <ProtectedRoute path="/vendors">
            <VendorDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id/edit"
        element={
          <ProtectedRoute path="/vendors">
            <VendorForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute path="/subscriptions">
            <Subscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import-subscriptions-plans"
        element={
          <ProtectedRoute path="/subscriptions">
            <ImportSubscriptionsPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions/:id"
        element={
          <ProtectedRoute path="/subscriptions">
            <SubscriptionDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-items/:id"
        element={
          <ProtectedRoute path="/subscriptions">
            <SubscriptionItemDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/economic-indices"
        element={
          <ProtectedRoute path="/subscriptions">
            <EconomicIndicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute path="/users">
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute path="/profile">
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute path="/settings">
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans"
        element={
          <ProtectedRoute path="/subscription-plans">
            <SubscriptionPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans/new"
        element={
          <ProtectedRoute path="/subscription-plans">
            <NewSubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans/edit/:id"
        element={
          <ProtectedRoute path="/subscription-plans">
            <EditSubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans/details/:id"
        element={
          <ProtectedRoute path="/subscription-plans">
            <SubscriptionPlanDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plan-families"
        element={
          <ProtectedRoute path="/subscription-plan-families">
            <SubscriptionPlanFamilies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-wizard/*"
        element={
          <ProtectedRoute path="/subscription-wizard">
            <SubscriptionWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-wizard/confirmation"
        element={
          <ProtectedRoute path="/subscription-wizard/step1">
            <SubscriptionConfirmation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-confirmation/:id"
        element={
          <ProtectedRoute>
            <SubscriptionConfirmation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions/wizard/Step1"
        element={
          <ProtectedRoute>
            <SubscriptionWizardStep1 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute path="/integrations">
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute path="/invoices">
            <Invoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedRoute path="/invoices">
            <InvoiceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute path="/courses">
            <Courses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/new"
        element={
          <ProtectedRoute path="/courses">
            <CourseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id"
        element={
          <ProtectedRoute path="/courses">
            <CourseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id/edit"
        element={
          <ProtectedRoute path="/courses">
            <CourseForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/course-families"
        element={
          <ProtectedRoute path="/course-families">
            <CourseFamilies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes"
        element={
          <ProtectedRoute path="/classes">
            <Classes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:id"
        element={
          <ProtectedRoute path="/classes">
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:id/sessions"
        element={
          <ProtectedRoute path="/classes">
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:id/sessions/edit/:sessionId"
        element={
          <ProtectedRoute path="/classes">
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:classId/sponsors"
        element={
          <ProtectedRoute path="/classes">
            <ClassSponsors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-sessions/:classId"
        element={
          <ProtectedRoute path="/classes">
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-sponsors"
        element={
          <ProtectedRoute path="/classes">
            <ClassSponsors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-details/:id"
        element={
          <ProtectedRoute path="/classes">
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instructors"
        element={
          <ProtectedRoute path="/instructors">
            <Instructors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-wizard/*"
        element={
          <ProtectedRoute path="/class-wizard">
            <ClassWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-wizard/confirmation"
        element={
          <ProtectedRoute path="/class-wizard">
            <ClassConfirmationPage />
          </ProtectedRoute>
        }
      />

      {/* Email routes */}
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute path="/email-templates">
            <EmailTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates/create"
        element={
          <ProtectedRoute path="/email-templates">
            <EmailTemplateCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates/:id"
        element={
          <ProtectedRoute path="/email-templates">
            <EmailTemplateEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates/:id/add-version"
        element={
          <ProtectedRoute path="/email-templates">
            <AddTemplateVersion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactional-emails"
        element={
          <ProtectedRoute path="/transactional-emails">
            <TransactionalEmails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates-email"
        element={
          <ProtectedRoute path="/templates-email">
            <ReactEmailPreview />
          </ProtectedRoute>
        }
      />

      {/* Notificações do Sistema */}
      <Route
        path="/system-notifications"
        element={
          <ProtectedRoute path="/system-notifications">
            <SystemNotifications />
          </ProtectedRoute>
        }
      />

      {/* Página de Controle de Acesso */}
      <Route
        path="/access-control"
        element={
          <ProtectedRoute path="/access-control">
            <AccessControl />
          </ProtectedRoute>
        }
      />

      {/* System Jobs */}
      <Route
        path="/system-jobs"
        element={
          <ProtectedRoute path="/system-jobs">
            <SystemJobs />
          </ProtectedRoute>
        }
      />

      {/* Task Management */}
      <Route
        path="/task-management"
        element={
          <ProtectedRoute path="/task-management">
            <TaskManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-management/settings"
        element={
          <ProtectedRoute path="/task-management">
            <TaskSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-management/labels"
        element={
          <ProtectedRoute path="/task-management">
            <TaskLabelsPage />
          </ProtectedRoute>
        }
      />

      {/* System Orders */}
      <Route
        path="/system-orders"
        element={
          <ProtectedRoute path="/system-orders">
            <SystemOrders />
          </ProtectedRoute>
        }
      />

      {/* Error routes */}
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={<NotFound />} />

      {/* New routes */}
      <Route
        path="/transactions"
        element={
          <ProtectedRoute path="/transactions">
            <Transactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/voucher-codes"
        element={
          <ProtectedRoute path="/voucher-codes">
            <VoucherCodes />
          </ProtectedRoute>
        }
      />

      {/* Redirects from /business/ paths to normal paths */}
      <Route
        path="/business/subscription-plans"
        element={<Navigate to="/subscription-plans" replace />}
      />
      <Route
        path="/business/subscription-plans/new"
        element={<Navigate to="/subscription-plans/new" replace />}
      />
      <Route
        path="/business/subscription-plans/edit/:id"
        element={<Navigate to="/subscription-plans/edit/:id" replace />}
      />
      <Route
        path="/business/subscription-plans/details/:id"
        element={<Navigate to="/subscription-plans/details/:id" replace />}
      />
      <Route
        path="/business/subscription-plan-families"
        element={<Navigate to="/subscription-plan-families" replace />}
      />
      <Route
        path="/business/import-subscriptions-plans"
        element={<Navigate to="/import-subscriptions-plans" replace />}
      />

      {/* Business Opportunities routes */}
      <Route
        path="/business-opportunities"
        element={
          <ProtectedRoute path="/business-opportunities">
            <BusinessOpportunities />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/new"
        element={
          <ProtectedRoute path="/business-opportunities">
            <BusinessOpportunityForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/:id/edit"
        element={
          <ProtectedRoute path="/business-opportunities">
            <BusinessOpportunityForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/:id"
        element={
          <ProtectedRoute path="/business-opportunities">
            <BusinessOpportunityDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/:id/simple"
        element={
          <ProtectedRoute path="/business-opportunities">
            <OpportunityDetailSimple />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposals/:id"
        element={
          <ProtectedRoute path="/proposals">
            <ProposalDetail />
          </ProtectedRoute>
        }
      />
      


      {/* Opportunity Wizard routes */}
      <Route
        path="/opportunity-wizard/*"
        element={
          <ProtectedRoute path="/opportunity-wizard">
            <OpportunityWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunity-wizard/confirmation/:id"
        element={
          <ProtectedRoute path="/opportunity-wizard">
            <OpportunityConfirmation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunity-confirmation/:id"
        element={
          <ProtectedRoute>
            <OpportunityConfirmation />
          </ProtectedRoute>
        }
      />

      {/* Rotas de contratos */}
      <Route
        path="/contracts"
        element={
          <ProtectedRoute path="/contracts">
            <Contracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts/:id"
        element={
          <ProtectedRoute path="/contracts">
            <ContractDetail />
          </ProtectedRoute>
        }
      />

      {/* Rotas para Templates de Contrato */}
      <Route
        path="/contract-templates"
        element={
          <ProtectedRoute path="/contract-templates">
            <ContractTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-templates/new"
        element={
          <ProtectedRoute path="/contract-templates">
            <ContractTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-templates/:id"
        element={
          <ProtectedRoute path="/contract-templates">
            <ContractTemplateForm />
          </ProtectedRoute>
        }
      />

      {/* Rotas para Templates de Proposta */}
      <Route
        path="/proposal-templates"
        element={
          <ProtectedRoute path="/proposal-templates">
            <ProposalTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal-templates/new"
        element={
          <ProtectedRoute path="/proposal-templates">
            <ProposalTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal-templates/:id"
        element={
          <ProtectedRoute path="/proposal-templates">
            <ProposalTemplateForm />
          </ProtectedRoute>
        }
      />

      {/* Nova implementação de Playbooks */}
      <Route 
        path="/playbooks" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/templates" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/templates/new" 
        element={
          <ProtectedRoute>
            <NewPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/templates/edit/:id" 
        element={
          <ProtectedRoute>
            <EditPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/runs" 
        element={
          <ProtectedRoute>
            <PlaybookRuns />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/runs/new" 
        element={
          <ProtectedRoute>
            <NewPlaybookRun />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/runs/:id" 
        element={
          <ProtectedRoute>
            <PlaybookRunDetail />
          </ProtectedRoute>
        }
      />

      {/* Customer Success Playbooks - Usando as páginas existentes */}
      <Route 
        path="/customer-success/playbooks" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/runs/:id" 
        element={
          <ProtectedRoute>
            <PlaybookRunDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/template/:id" 
        element={
          <ProtectedRoute>
            <EditPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/templates" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/edit-template/:id" 
        element={
          <ProtectedRoute>
            <EditPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/runs" 
        element={
          <ProtectedRoute>
            <PlaybookRuns />
          </ProtectedRoute>
        } 
      />

      {/* Rota para exemplos de loading */}
      <Route 
        path="/loading-examples" 
        element={
          <ProtectedRoute>
            <LoadingExamples />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

export default AppRoutes; 